import React, { useEffect, useState } from 'react';
import { Button, Empty, Form, Input, InputNumber, message, Modal, Select, Spin } from 'antd';
import { checkGrowUpBenefitApi, queryGrowUpPetListApi, sendGrowUpBenefitsApi } from '@/api/member/member';
import styles from '../../index.module.scss';

type Props = {
  shopId?: number | null;
  oneId: string;
  shopUserId?: string | null;
  active: boolean;
};

type GrowUpType = 'ADULT' | 'OLD';

const sendTypeOptions = [
  { label: '积分', value: 'POINT' },
  { label: '其他', value: 'THIRD_PLAT' },
];

const fallbackPetAvatar = 'https://img.alicdn.com/imgextra/i3/155168396/O1CN01HvRVMi2BtQTGD5WA9_!!155168396.png';

const GrowUpTab: React.FC<Props> = ({ shopId, oneId, shopUserId, active }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [eligible, setEligible] = useState<boolean>(false);
  const [petList, setPetList] = useState<any[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<GrowUpType>('ADULT');
  const [currentPet, setCurrentPet] = useState<any>(null);
  const sendType = Form.useWatch('sendType', form) || 'POINT';

  useEffect(() => {
    setEligible(false);
    setPetList([]);
    setInitialized(false);
    setConfirmOpen(false);
    setCurrentPet(null);
    form.resetFields();
  }, [shopId, oneId, shopUserId, form]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !shopId || !shopUserId || initialized) return;
    fetchData();
    setInitialized(true);
  }, [active, initialized, shopId, shopUserId]);

  const fetchData = async () => {
    if (!shopId || !shopUserId) return;
    setLoading(true);
    try {
      const [checkRes, listRes] = await Promise.all([
        checkGrowUpBenefitApi(shopId, { shopUserId }),
        queryGrowUpPetListApi(shopId, { shopUserId }),
      ]);
      if (checkRes?.code === 200) {
        setEligible(Boolean(checkRes.data));
      }
      if (listRes?.code === 200) {
        setPetList(listRes.data || []);
      }
    } catch (error) {
      // network.ts handles error message globally
    } finally {
      setLoading(false);
    }
  };

  const canConfirmAdult = (pet: any) => Boolean(eligible && pet?.isAdultBenefit && !pet?.adultSendFlag);
  const canConfirmOld = (pet: any) => Boolean(eligible && pet?.isOldBenefit && !pet?.oldSendFlag);

  const openConfirm = (type: GrowUpType, pet: any) => {
    setConfirmType(type);
    setCurrentPet(pet);
    form.setFieldsValue({
      sendType: 'POINT',
      sendValue: undefined,
    });
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setCurrentPet(null);
    form.resetFields();
  };

  const handleConfirmSend = async () => {
    if (!shopId || !oneId || !currentPet?.petOneId) return;
    try {
      const values = await form.validateFields();
      setSending(true);
      const payload = {
        growUpType: confirmType,
        oneId,
        petOneId: currentPet.petOneId,
        sendType: values.sendType,
        sendValue: String(values.sendValue).trim(),
        shopId: String(shopId),
        shopUserId: shopUserId || undefined,
      } as const;
      const res = await sendGrowUpBenefitsApi(shopId, payload);
      if (res?.code === 200) {
        message.success('发放成功');
        closeConfirm();
        fetchData();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      // network.ts handles error message globally
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles['growup-section']}>
      <Spin spinning={loading}>
        <div
          className={`${styles['growup-eligible-box']} ${
            eligible ? styles['growup-eligible-box--yes'] : styles['growup-eligible-box--no']
          }`}>
          {eligible ? '符合礼领取条件' : '不符合礼领取条件'}
        </div>

        <div className={styles['growup-title']}>所有宠物</div>
        {petList.length ? (
          <div className={styles['growup-list']}>
            {petList.map((pet: any) => (
              <div
                key={pet.petOneId || pet.petNickName}
                className={styles['growup-card']}>
                <div className={styles['growup-card__pet']}>
                  <img
                    src={pet.petJdImg || pet.petTmImg || fallbackPetAvatar}
                    alt={pet.petNickName || 'pet'}
                  />
                  <div className={styles['growup-card__pet-meta']}>
                    <div className={styles['growup-card__pet-name']}>
                      <span>{pet.petNickName || '-'}</span>
                      <span>{pet.petBreedName || '-'}</span>
                    </div>
                    <div>{`生日：${pet.birthday || '-'}`}</div>
                    <div>{`${pet.age || '-'} | ${pet.growthStage || '-'}`}</div>
                    <div>{`创建时间：${pet.createTime || '-'}`}</div>
                  </div>
                </div>

                <div className={styles['growup-card__reward']}>
                  <div className={styles['growup-card__period']}>{`进入成年期：${pet.adultChangeDate || '-'}`}</div>
                  <div className={styles['growup-card__status-row']}>
                    <span>成年礼：</span>
                    <span
                      className={`${styles['growup-card__tag']} ${
                        pet.adultSendFlag ? styles['growup-card__tag--sent'] : styles['growup-card__tag--pending']
                      }`}>
                      {pet.adultSendFlag ? '已发放' : '未发放'}
                    </span>
                  </div>
                  {canConfirmAdult(pet) && (
                    <Button
                      type="primary"
                      className={styles['growup-card__confirm-btn']}
                      onClick={() => openConfirm('ADULT', pet)}>
                      确认已发权益
                    </Button>
                  )}
                  {pet.adultSendFlag && (
                    <div className={styles['growup-card__time']}>{`发放时间：${pet.adultSendTime || '-'}`}</div>
                  )}
                </div>

                <div className={styles['growup-card__reward']}>
                  <div className={styles['growup-card__period']}>{`进入老年期：${pet.oldChangeDate || '-'}`}</div>
                  <div className={styles['growup-card__status-row']}>
                    <span>老友礼：</span>
                    <span
                      className={`${styles['growup-card__tag']} ${
                        pet.oldSendFlag ? styles['growup-card__tag--sent'] : styles['growup-card__tag--pending']
                      }`}>
                      {pet.oldSendFlag ? '已发放' : '未发放'}
                    </span>
                  </div>
                  {canConfirmOld(pet) && (
                    <Button
                      type="primary"
                      className={styles['growup-card__confirm-btn']}
                      onClick={() => openConfirm('OLD', pet)}>
                      确认已发权益
                    </Button>
                  )}
                  {pet.oldSendFlag && (
                    <div className={styles['growup-card__time']}>{`发放时间：${pet.oldSendTime || '-'}`}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="暂无成长礼宠物数据" />
        )}
      </Spin>

      <Modal
        title="确认发放权益"
        open={confirmOpen}
        onCancel={closeConfirm}
        onOk={handleConfirmSend}
        okText="确认"
        cancelText="取消"
        confirmLoading={sending}
        destroyOnHidden>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ style: { width: 96 } }}>
          <Form.Item
            label="权益类型"
            name="sendType"
            initialValue="POINT"
            rules={[{ required: true, message: '请选择权益类型' }]}>
            <Select
              options={sendTypeOptions}
              onChange={() => form.setFieldsValue({ sendValue: undefined })}
            />
          </Form.Item>
          <Form.Item
            label={sendType === 'POINT' ? '发放额度' : '权益名称'}
            name="sendValue"
            rules={[
              { required: true, message: sendType === 'POINT' ? '请输入发放额度' : '请输入权益名称' },
              {
                validator: (_, value) => {
                  if (sendType !== 'POINT') return Promise.resolve();
                  if (value === undefined || value === null || value === '') return Promise.resolve();
                  return /^[1-9]\d*$/.test(String(value))
                    ? Promise.resolve()
                    : Promise.reject(new Error('请输入大于0的正整数'));
                },
              },
            ]}>
            {sendType === 'POINT' ? (
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={0}
                controls={false}
                placeholder="请输入"
              />
            ) : (
              <Input placeholder="请输入" />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GrowUpTab;
